import { ChevronRight, Check, Clock, Pencil } from 'lucide-react';
import {
  contentSignedCounts,
  docSignedCounts,
  initials,
  isCurrentUserSectionOwner,
  unifiedStatus,
  type ReadinessRow,
  type StatusKind,
  type UnifiedStatus,
} from './documentReadinessHelpers';
import type { UserOut } from '@/types/api';

interface CurrentUserNameRefs {
  displayName: string | null;
  email: string | null;
}

const STATUS_PILL_CLS: Record<StatusKind, string> = {
  signed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  warn: 'bg-amber-50 text-amber-700 border-amber-200',
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  progress: 'bg-slate-50 text-slate-600 border-slate-200',
  unassigned: 'bg-slate-50 text-slate-500 border-slate-200',
};

function StatusPill({ status }: { status: UnifiedStatus }) {
  const cls = STATUS_PILL_CLS[status.kind];
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${cls}`}
    >
      {status.label}
    </span>
  );
}

function SectionBadge({ section }: { section: string }) {
  return <span className="font-mono text-[11px] text-slate-500">{section}</span>;
}

// Deliberate hard ceiling so the Document column stays compact on wide
// monitors regardless of viewport. CSS `truncate` on the inner button
// handles narrower viewports — its ellipsis transparently replaces the
// JS-added one, so users only ever see one ellipsis.
const MAX_DOC_NAME_CHARS = 50;
function truncateDocName(name: string): string {
  return name.length > MAX_DOC_NAME_CHARS ? `${name.slice(0, MAX_DOC_NAME_CHARS)}…` : name;
}

function Avatar({
  name,
  size = 26,
  state,
  isSelf = false,
}: {
  name: string;
  size?: number;
  state?: 'pending' | 'signed';
  isSelf?: boolean;
}) {
  const fontSize = Math.max(9, Math.round(size * 0.4));
  const bgCls = isSelf ? 'bg-brand-orange' : 'bg-slate-500';
  return (
    <span
      className={`relative inline-grid place-items-center rounded-full font-semibold text-white ring-2 ring-white ${bgCls}`}
      style={{ width: size, height: size, fontSize }}
      title={name}
    >
      {initials(name)}
      {state === 'signed' && (
        <span className="absolute -right-1 -bottom-1 grid h-3.5 w-3.5 place-items-center rounded-full bg-emerald-500 ring-2 ring-white">
          <Check className="h-2 w-2 text-white" strokeWidth={3} />
        </span>
      )}
      {state === 'pending' && (
        <span className="absolute -right-0.5 -bottom-0.5 h-2.5 w-2.5 rounded-full bg-amber-500 ring-2 ring-white" />
      )}
    </span>
  );
}

function PercentCell({
  signed,
  total,
  emptyTooltip,
}: {
  signed: number;
  total: number;
  emptyTooltip?: string;
}) {
  if (total === 0) {
    return (
      <span
        className="text-base font-semibold text-slate-400"
        title={emptyTooltip}
      >
        —
      </span>
    );
  }
  const pct = Math.round((100 * signed) / total);
  const toneCls =
    pct === 100 ? 'text-emerald-600' : pct === 0 ? 'text-red-600' : 'text-amber-600';
  return (
    <span className="inline-flex items-baseline gap-1.5">
      <span className={`text-base font-semibold tabular-nums ${toneCls}`}>{pct}%</span>
      <span className="font-mono text-[10px] text-slate-400 tabular-nums">
        {signed}/{total}
      </span>
    </span>
  );
}

function ExpandedRow({
  doc,
  currentUserId,
  currentUserNameRefs,
  canEdit,
  onSignOff,
  onEditOwners,
  usersById,
}: {
  doc: ReadinessRow;
  currentUserId: string | null;
  currentUserNameRefs: CurrentUserNameRefs;
  canEdit: boolean;
  onSignOff: (docId: string) => void;
  onEditOwners: (docId: string) => void;
  usersById: Map<string, UserOut>;
}) {
  const c = contentSignedCounts(doc);
  const d = docSignedCounts(doc);

  return (
    <div className="border-t border-slate-100 bg-slate-50/40 px-5 py-4">
      {/* Two-column body */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1.4fr_1fr]">
        {/* CONTENT — per-section sign-offs */}
        <div className="rounded-lg border border-slate-200 bg-white p-3">
          <div className="mb-2 flex items-center gap-2 text-xs">
            <span className="inline-flex h-5 w-5 items-center justify-center rounded bg-slate-100 font-mono text-[10px] font-bold text-slate-700">
              C
            </span>
            <span className="font-semibold text-brand-charcoal">
              Content sign-offs · per section
            </span>
            <span className="ml-auto font-mono text-[11px] text-slate-500">
              {c.signed}/{c.total}
            </span>
          </div>
          {doc.sections.length === 0 ? (
            <div className="rounded-md border border-dashed border-slate-300 bg-slate-50/40 px-3 py-4 text-center">
              <p className="text-sm font-medium text-brand-charcoal">No content sign-offs</p>
              <p className="mt-1 text-[11px] text-slate-500">
                Use Chrona Add-in to assign a owner to sections
              </p>
            </div>
          ) : (
            <div className="space-y-1.5">
              {doc.sections.map((s) => {
                // Section-row render rules (locked, A2F-662):
                //   • Resolved org member → avatar + displayName + isMe via id.
                //   • Unmatched ownerName → name-only fallback; isMe derives
                //     from the name vs currentUser.displayName/email.
                //   • No owner at all → "?" avatar + "Unassigned" name.
                // Layout matches the DOCUMENT side row exactly so the two
                // sign-off tracks read as the same kind of object. Role
                // sub-line is hardcoded to "contributor — addin" until
                // section ownership flows through real org-user records.
                const u = s.ownerUserId ? usersById.get(s.ownerUserId) : null;
                const hasName = !!s.ownerName;
                const displayName = u?.displayName ?? s.ownerName ?? 'Unassigned';
                const isMe =
                  (!!currentUserId && s.ownerUserId === currentUserId) ||
                  isCurrentUserSectionOwner(s.ownerName, currentUserNameRefs);
                const signed = s.state === 'signed';
                return (
                  <div
                    key={s.id}
                    className={`flex items-center gap-2.5 rounded-md border px-2.5 py-2 ${
                      signed
                        ? 'border-emerald-200 bg-emerald-50/40'
                        : !hasName
                          ? 'border-dashed border-slate-300 bg-slate-50/40'
                          : isMe
                            ? 'border-brand-orange/30 bg-orange-50/30'
                            : 'border-slate-200 bg-white'
                    }`}
                  >
                    <Avatar name={displayName} size={32} isSelf={isMe} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-brand-charcoal">
                        {displayName}
                        {isMe && (
                          <span className="ml-1 rounded bg-orange-50 px-1 py-px text-[9px] font-bold tracking-wider text-brand-orange">
                            YOU
                          </span>
                        )}
                      </p>
                      <p className="mt-0.5 truncate text-[11px] text-slate-500">
                        contributor — addin
                      </p>
                    </div>
                    {!hasName ? (
                      <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                        Unassigned
                      </span>
                    ) : signed ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-700">
                        <Check className="h-3 w-3" /> Signed off
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-700">
                        <Clock className="h-3 w-3" /> Pending
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* DOCUMENT — per-owner sign-offs */}
        <div className="rounded-lg border border-slate-200 bg-white p-3">
          <div className="mb-2 flex items-center gap-2 text-xs">
            <span className="inline-flex h-5 w-5 items-center justify-center rounded bg-slate-100 font-mono text-[10px] font-bold text-slate-700">
              D
            </span>
            <span className="font-semibold text-brand-charcoal">Document sign-offs</span>
            <span className="font-mono text-[11px] text-slate-500">
              {d.signed}/{d.total}
            </span>
            {canEdit && (
              <button
                type="button"
                onClick={() => onEditOwners(doc.id)}
                className="ml-auto inline-flex items-center gap-1 rounded text-[11px] font-semibold text-brand-orange hover:underline"
              >
                <Pencil className="h-3 w-3" /> Edit owners
              </button>
            )}
          </div>
          {doc.docOwners.length === 0 ? (
            <div className="rounded-md border border-dashed border-slate-300 bg-slate-50/40 px-3 py-4 text-center">
              <p className="text-sm font-medium text-brand-charcoal">No document owners assigned</p>
              <p className="mt-1 text-[11px] text-slate-500">
                This document is in <b className="font-semibold text-brand-charcoal">Unassigned</b>{' '}
                state and will not advance to sign-off.
              </p>
              {canEdit && (
                <button
                  type="button"
                  onClick={() => onEditOwners(doc.id)}
                  className="mt-2.5 rounded-md bg-brand-orange px-3 py-1 text-xs font-semibold text-white hover:bg-orange-600"
                >
                  + Add owners
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-1.5">
              {doc.docOwners.map((o) => {
                const u = usersById.get(o.userId);
                const name = u?.displayName ?? '?';
                const isMe = !!currentUserId && o.userId === currentUserId;
                return (
                  <div
                    key={o.userId}
                    className={`flex items-center gap-2.5 rounded-md border px-2.5 py-2 ${
                      o.state === 'signed'
                        ? 'border-emerald-200 bg-emerald-50/40'
                        : isMe
                          ? 'border-brand-orange/30 bg-orange-50/30'
                          : 'border-slate-200 bg-white'
                    }`}
                  >
                    <Avatar name={name} size={32} isSelf={isMe} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-brand-charcoal">
                        {name}
                        {isMe && (
                          <span className="ml-1 rounded bg-orange-50 px-1 py-px text-[9px] font-bold tracking-wider text-brand-orange">
                            YOU
                          </span>
                        )}
                      </p>
                      <p className="mt-0.5 truncate text-[11px] text-slate-500">
                        {u?.role ?? 'unknown role'}
                      </p>
                    </div>
                    {o.state === 'signed' ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-700">
                        <Check className="h-3 w-3" /> Signed off
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-700">
                        <Clock className="h-3 w-3" /> Pending
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

interface TableProps {
  docs: ReadinessRow[];
  currentUserId: string | null;
  /**
   * Current user's displayName + email — used by section cards for the
   * isMe-by-name fallback when a Word-comment author can't be matched to
   * an org-member id (locked rules in `isCurrentUserSectionOwner`).
   */
  currentUserNameRefs: CurrentUserNameRefs;
  usersById: Map<string, UserOut>;
  canEdit: boolean;
  expandedIds: Set<string>;
  onToggleExpand: (docId: string) => void;
  onSignOff: (docId: string) => void;
  onEditOwners: (docId: string) => void;
  onOpenDoc: (docId: string) => void;
}

export function DocumentReadinessTable({
  docs,
  currentUserId,
  currentUserNameRefs,
  usersById,
  canEdit,
  expandedIds,
  onToggleExpand,
  onSignOff,
  onEditOwners,
  onOpenDoc,
}: TableProps) {
  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <table className="w-full">
        <thead>
          <tr className="bg-slate-50">
            <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-slate-500" style={{ width: '26%' }}>
              Document
            </th>
            <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-slate-500" style={{ width: 110 }}>
              Section
            </th>
            <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-slate-500" style={{ width: 140 }}>
              % Content Done
            </th>
            <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-slate-500" style={{ width: 140 }}>
              % Document Done
            </th>
            <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-slate-500" style={{ width: 220 }}>
              Status
            </th>
            <th className="px-4 py-2.5" style={{ width: 130 }} />
          </tr>
        </thead>
        <tbody>
          {docs.map((d) => {
            const expanded = expandedIds.has(d.id);
            const status = unifiedStatus(d);
            const cs = contentSignedCounts(d);
            const ds = docSignedCounts(d);
            const myAssignment = currentUserId
              ? d.docOwners.find((o) => o.userId === currentUserId)
              : undefined;
            const actionable = !!myAssignment && myAssignment.state !== 'signed';
            return (
              <FragmentRow key={d.id}>
                <tr
                  className={`border-t border-slate-100 ${
                    expanded ? 'bg-slate-50/60' : actionable ? 'hover:bg-orange-50/30' : 'hover:bg-slate-50/60'
                  } ${actionable ? 'border-l-2 border-l-brand-orange' : ''}`}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <button
                        type="button"
                        onClick={() => onToggleExpand(d.id)}
                        aria-label={expanded ? 'Collapse' : 'Expand'}
                        className="grid h-6 w-6 shrink-0 place-items-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                      >
                        <ChevronRight
                          className={`h-3.5 w-3.5 transition-transform ${expanded ? 'rotate-90' : ''}`}
                        />
                      </button>
                      <div className="min-w-0">
                        <button
                          type="button"
                          onClick={() => onOpenDoc(d.id)}
                          title={d.name}
                          className="block w-full truncate text-left text-sm font-medium text-brand-charcoal hover:text-brand-orange hover:underline"
                        >
                          {truncateDocName(d.name)}
                        </button>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <SectionBadge section={d.section} />
                  </td>
                  <td className="px-4 py-3">
                    <PercentCell
                      signed={cs.signed}
                      total={cs.total}
                      emptyTooltip="No section comments in this submission's documents — nothing to sign."
                    />
                  </td>
                  <td className="px-4 py-3">
                    <PercentCell
                      signed={ds.signed}
                      total={ds.total}
                      emptyTooltip="No document owners assigned. Use the expanded row to add owners."
                    />
                  </td>
                  <td className="px-4 py-3">
                    <StatusPill status={status} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    {myAssignment && myAssignment.state !== 'signed' ? (
                      <button
                        type="button"
                        onClick={() => onSignOff(d.id)}
                        className="inline-flex items-center gap-1 rounded-md bg-brand-orange px-2.5 py-1 text-[11px] font-semibold text-white shadow-sm hover:bg-orange-600"
                      >
                        <Check className="h-3 w-3" /> Sign off
                      </button>
                    ) : myAssignment && myAssignment.state === 'signed' ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-700">
                        <Check className="h-3 w-3" /> Signed
                      </span>
                    ) : null}
                  </td>
                </tr>
                {expanded && (
                  <tr>
                    <td colSpan={6} className="p-0">
                      <ExpandedRow
                        doc={d}
                        currentUserId={currentUserId}
                        currentUserNameRefs={currentUserNameRefs}
                        canEdit={canEdit}
                        onSignOff={onSignOff}
                        onEditOwners={onEditOwners}
                        usersById={usersById}
                      />
                    </td>
                  </tr>
                )}
              </FragmentRow>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// React.Fragment doesn't accept a key in the table-row position without
// a small wrapper — keep it inline for clarity.
function FragmentRow({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}
